/* eslint-disable react/require-default-props */
import { StandardTagger, ListboxOption, brandfolderColorPalette } from '@brandfolder/react';
import { t } from '@lingui/macro';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';

import { getIdentities, IdentityResponse } from '@api/v4/private/people_tagging/faces';
interface PersonFilter {
  id: string;
  name: string;
}

interface PeopleFilterProps {
  filters: {
    people?: PersonFilter[];
  };
  updateFilters: (id: string, name: string) => void;
}

const FaceImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  flex-shrink: 0;
  object-fit: cover;
`;

const TagAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  flex-shrink: 0;
  object-fit: cover;
`;

const TagContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TagName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${brandfolderColorPalette.neutrals[700]};
`;

const FaceCount = styled.span`
  font-size: 14px;
  color: ${brandfolderColorPalette.neutrals[400]};
`;

const Checkbox = styled.span<{ $checked: boolean }>`
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid ${({ $checked }) => ($checked ? brandfolderColorPalette.primary[600] : brandfolderColorPalette.neutrals[300])};
  border-radius: 4px;
  background: ${({ $checked }) => ($checked ? brandfolderColorPalette.primary[600] : brandfolderColorPalette.neutrals[0])};
  flex-shrink: 0;
  position: relative;

  &::after {
    content: ${({ $checked }) => ($checked ? "''" : 'none')};
    position: absolute;
    left: 5px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const PeopleFilterWrapper = styled.div`
  padding: 20px 25px;
  border-bottom: 1px solid ${brandfolderColorPalette.neutrals[300]};

  textarea.bf-combobox__combobox {
    width: calc(100% - 10px);
  }

  .bf-textfield__body{
    .bf-textfield__addons{
      .bf-textarea__fake{
        min-height: fit-content;
        height: 48px;
        padding: 0;
        .bf-textfield__wrapper{
          height: 100%;
        }
      }
    }
  }

  /* Chevron arrow styling */
  .bf-textarea__fake {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      right: 16px;
      top: 50%;
      width: 9px;
      height: 9px;
      border-right: 2px solid ${brandfolderColorPalette.neutrals[700]};
      border-bottom: 2px solid ${brandfolderColorPalette.neutrals[700]};
      transform: translateY(-70%) rotate(45deg);
      transition: transform 0.2s ease;
      pointer-events: none;
    }
  }

  /* Rotate chevron when dropdown is open */
  .bf-textarea__fake:has(.bf-combobox[aria-expanded='true'])::after {
    transform: translateY(-30%) rotate(-135deg);
  }

  .bf-listbox__standard {
    width: 100%;
    z-index: 2;

    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: ${brandfolderColorPalette.neutrals[100]};
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${brandfolderColorPalette.neutrals[300]};
      border-radius: 4px;

      &:hover {
        background: ${brandfolderColorPalette.neutrals[400]};
      }
    }

    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: ${brandfolderColorPalette.neutrals[300]} ${brandfolderColorPalette.neutrals[100]};
  }

  /* Hide checkbox inside tags/pills - checkbox should only show in dropdown */
  .bf-pill,
  .bf-tag {
    .bf-pill__content > div > ${Checkbox},
    .bf-tag__label > div > ${Checkbox} {
      display: none;
    }
  }

  /* Tag styling to match the design */
  .bf-pill,
  .bf-tag {
    background: ${brandfolderColorPalette.primary[100]} !important;
    border: none !important;
    border-radius: 4px !important;
    padding: 4px 8px !important;
    gap: 8px !important;
    height: auto !important;

    .bf-pill__content,
    .bf-tag__label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 500;
      color: ${brandfolderColorPalette.neutrals[700]};
    }

    .bf-pill__close-button,
    .bf-tag__remove {
      color: ${brandfolderColorPalette.neutrals[500]};
      font-size: 16px;

      &:hover {
        color: ${brandfolderColorPalette.neutrals[700]};
      }
    }
  }
`;

const PeopleFilter: React.FunctionComponent<PeopleFilterProps> = ({
  filters,
  updateFilters,
}) => {
  const [availableIdentities, setAvailableIdentities] = useState<IdentityResponse[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [clearTags, setClearTags] = useState(false);
  // Local state to track selected IDs - single source of truth for checkbox state
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>([]);
  // Key to force StandardTagger re-render when selection changes
  const [taggerKey, setTaggerKey] = useState(0);
  // filters.people now stores objects with {id, name}
  const selectedPeople = filters.people || [];
  const shouldReopenDropdown = useRef(false);

  // Sync local state with external filters.people (for external changes like Clear Filters)
  // Extract IDs from the objects for internal tracking
  useEffect(() => {
    const idsFromPeople = selectedPeople.map((p) => p.id);
    setLocalSelectedIds(idsFromPeople);
    setTaggerKey((k) => k + 1);

    if (selectedPeople.length === 0) {
      setClearTags(true);
    }
  }, [selectedPeople.map((p) => p.id).join(',')]);

  // Reset clearTags after it's been applied
  useEffect(() => {
    if (clearTags) {
      setClearTags(false);
    }
  }, [clearTags]);

  useEffect(() => {
    const fetchPeopleData = async (): Promise<void> => {
      try {
        const brandfolderKey = BFG.brandfolder_key;
        if (!brandfolderKey) {
          return;
        }

        const response = await getIdentities({ brandfolderKey });

        const identities: IdentityResponse[] = response
          .filter((identity) => identity.name && identity.name.trim() !== '' && (!identity.metadata || JSON.parse(identity.metadata)?.hide !== true))
          .sort((a, b) => (b.face_count || 0) - (a.face_count || 0));

        setAvailableIdentities(identities);

      } catch (error) {
        console.error('Error fetching people data:', error);
      }
    };

    fetchPeopleData();
  }, []);

  const handleTaggedChange = (tags: ListboxOption[]): void => {
    try {
      const newIds = tags
        .filter((tag) => tag && tag.value != null)
        .map((tag) => typeof tag.value === 'string' ? tag.value : String(tag.value));

      // Update local state immediately for checkbox sync
      setLocalSelectedIds(newIds);

      // Find the difference to determine what was added or removed
      const addedId = newIds.find((id) => !localSelectedIds.includes(id));
      const removedId = localSelectedIds.find((id) => !newIds.includes(id));

      const changedId = addedId || removedId;
      if (changedId) {
        const identity = availableIdentities.find((i) => i.id === changedId);
        if (identity) {
          updateFilters(changedId, identity.name);
        }
      }
    } catch (error) {
      console.error('Error in handleTaggedChange:', error);
    }
  };

  const handleOptionClick = useCallback((identity: IdentityResponse, isChecked: boolean, e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();

    // Toggle selection
    if (isChecked) {
      // Remove from selection
      setLocalSelectedIds((prev) => prev.filter((id) => id !== identity.id));
    } else {
      // Add to selection
      setLocalSelectedIds((prev) => [...prev, identity.id]);
    }

    // Clear search value so dropdown shows all options when reopened
    setSearchValue('');

    // Update parent filters - this will trigger the sync effect which handles re-keying
    updateFilters(identity.id, identity.name);
  }, [updateFilters]);

  const taggerOptions: ListboxOption[] = useMemo(() => {
    return availableIdentities.map((identity) => {
      // Use localSelectedIds for checkbox state - stays in sync with StandardTagger
      const isChecked = localSelectedIds.includes(identity.id);
      return {
        key: identity.id,
        label: identity.name,
        value: identity.id,
        children: (
          <div
            className="people-filter-option"
            onClick={(e) => handleOptionClick(identity, isChecked, e)}
            role="button"
            tabIndex={0}
          >
            <Checkbox $checked={isChecked} />
            <FaceImage alt={identity.name} src={identity.identity_thumbnail_url} />
            <span>{identity.name}</span>
            {identity.face_count !== undefined && identity.face_count > 0 && (
              <FaceCount>{identity.face_count}</FaceCount>
            )}
          </div>
        ),
      };
    });
  }, [availableIdentities, localSelectedIds, handleOptionClick]);

  const filteredOptions: ListboxOption[] = useMemo(() => {
    if (!searchValue.trim()) {
      return taggerOptions;
    }
    const query = searchValue.toLowerCase();
    return taggerOptions.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [taggerOptions, searchValue]);

  const initialTags: ListboxOption[] = useMemo(() => {
    // Use localSelectedIds as source of truth for tags
    return localSelectedIds
      .map((id) => {
        const identity = availableIdentities.find((i) => i.id === id);
        if (identity) {
          return {
            key: identity.id,
            label: identity.name,
            value: identity.id,
            children: (
              <TagContent>
                <TagAvatar alt={identity.name} src={identity.identity_thumbnail_url} />
                <TagName>{identity.name}</TagName>
              </TagContent>
            ),
          };
        }
        return null;
      })
      .filter(Boolean) as ListboxOption[];
  }, [localSelectedIds, availableIdentities]);

  if (availableIdentities.length === 0) {
    return null;
  }

  return (
    <PeopleFilterWrapper>
      <StandardTagger
        key={`people-tagger-${taggerKey}`}
        allowCustomValue={false}
        allowDuplicates={false}
        clearTags={clearTags}
        comboboxProps={{
          comboboxTextareaProps: {
            onChange: (e) => {
              setSearchValue(e.target.value);
            },
            placeholder: t`Select people...`,
          }
        }}
        id="people-filter-tagger"
        initialTags={initialTags}
        labels={{
          label: t`People`,
          emptyLabel: t`No people found`,
          listboxLabel: t`List of people`,
          removeTagLabel: t`Remove person`,
          iconButtonLabel: t`Toggle list of people`,
        }}
        onTagged={(tags) => handleTaggedChange(tags)}
        options={filteredOptions}
        search={false}
        showLabel={true}
      />
    </PeopleFilterWrapper>
  );
};

export default PeopleFilter;
